import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { 
  DollarSign, FileText, Clock, CheckCircle, XCircle, 
  AlertTriangle, Calendar, Users, ChevronRight, RefreshCw,
  Download, Filter, Search, Plus, Calculator, Loader2,
  ArrowRight, TrendingUp, TrendingDown
} from 'lucide-react';
import { toast } from 'sonner';

export default function BillingManagement() {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [claims, setClaims] = useState([]);
  const [rates, setRates] = useState([]);
  const [authorizations, setAuthorizations] = useState([]);
  const [selectedClaim, setSelectedClaim] = useState(null);
  const [generatingClaims, setGeneratingClaims] = useState(false);
  const [dateRange, setDateRange] = useState({
    start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });
  const [unitCalculator, setUnitCalculator] = useState({ minutes: '', result: null });

  useEffect(() => {
    loadDashboard();
  }, []);

  useEffect(() => {
    if (activeTab === 'claims') loadClaims();
    if (activeTab === 'rates') loadRates();
    if (activeTab === 'authorizations') loadAuthorizations();
  }, [activeTab]);

  const loadDashboard = async () => {
    try {
      const [summaryRes, unbilledRes] = await Promise.all([
        api.get('/claims/dashboard/summary'),
        api.get('/claims/dashboard/unbilled-shifts')
      ]);
      setDashboardData({
        ...summaryRes.data,
        ...unbilledRes.data
      });
    } catch (error) {
      console.error('Error loading dashboard:', error);
      // Set empty dashboard data with setup required flag
      setDashboardData({
        claims_by_status: {},
        total_claims: 0,
        total_billed: 0,
        total_paid: 0,
        outstanding: 0,
        unbilled_count: 0,
        tables_ready: false,
        setup_required: "Medical billing tables need to be created in Supabase"
      });
    } finally {
      setLoading(false);
    }
  };

  const loadClaims = async () => {
    try {
      const response = await api.get('/claims');
      setClaims(response.data.claims || []);
    } catch (error) {
      console.error('Error loading claims:', error);
    }
  };

  const loadRates = async () => {
    try {
      const response = await api.get('/claims/rates/list');
      setRates(response.data.rates || []);
    } catch (error) {
      console.error('Error loading rates:', error);
    }
  };

  const loadAuthorizations = async () => {
    try {
      const response = await api.get('/claims/authorizations');
      setAuthorizations(response.data.authorizations || []);
    } catch (error) {
      console.error('Error loading authorizations:', error);
    }
  };

  const generateClaims = async () => {
    setGeneratingClaims(true);
    try {
      const response = await api.post('/claims/generate', {
        start_date: dateRange.start,
        end_date: dateRange.end
      });
      
      if (response.data.claims_generated > 0) {
        toast.success(`Generated ${response.data.claims_generated} claims totaling $${response.data.total_amount.toFixed(2)}`);
        loadClaims();
        loadDashboard();
      } else {
        toast.info(response.data.message);
      }
    } catch (error) {
      console.error('Error generating claims:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate claims');
    } finally {
      setGeneratingClaims(false);
    }
  };

  const calculateUnits = async () => {
    if (!unitCalculator.minutes) return;
    try {
      const response = await api.get(`/claims/calculate-units?minutes=${unitCalculator.minutes}`);
      setUnitCalculator(prev => ({ ...prev, result: response.data }));
    } catch (error) {
      console.error('Error calculating units:', error);
    }
  };

  const updateClaimStatus = async (claimId, newStatus) => {
    try {
      await api.put(`/claims/${claimId}/status`, { status: newStatus });
      toast.success(`Claim status updated to ${newStatus}`);
      loadClaims();
      loadDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  const exportEDI = async (claimId) => {
    try {
      const response = await api.get(`/claims/${claimId}/export/edi`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `837P_${claimId.slice(0,8)}_${new Date().toISOString().split('T')[0]}.edi`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('EDI file downloaded');
    } catch (error) {
      toast.error('Failed to export EDI');
    }
  };

  const exportInvoice = async (claimId) => {
    try {
      const response = await api.get(`/claims/${claimId}/export/invoice`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Invoice_${claimId.slice(0,8)}_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Invoice PDF downloaded');
    } catch (error) {
      toast.error('Failed to export invoice');
    }
  };

  const exportBatchEDI = async () => {
    try {
      const response = await api.get(`/claims/export/edi/batch?status=pending`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `837P_BATCH_${new Date().toISOString().split('T')[0]}.edi`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Batch EDI file downloaded');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'No pending claims to export');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'bg-gray-100 text-gray-700',
      pending: 'bg-yellow-100 text-yellow-700',
      submitted: 'bg-blue-100 text-blue-700',
      accepted: 'bg-green-100 text-green-700',
      rejected: 'bg-red-100 text-red-700',
      paid: 'bg-emerald-100 text-emerald-700',
      denied: 'bg-red-100 text-red-700',
      void: 'bg-gray-100 text-gray-500'
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: TrendingUp },
    { id: 'claims', label: 'Claims', icon: FileText },
    { id: 'rates', label: 'Billing Rates', icon: DollarSign },
    { id: 'authorizations', label: 'Authorizations', icon: CheckCircle },
    { id: 'calculator', label: 'Unit Calculator', icon: Calculator }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Tab Navigation */}
      <div className="flex gap-2 border-b border-gray-200 pb-2 overflow-x-auto">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg whitespace-nowrap transition-colors ${
              activeTab === tab.id 
                ? 'bg-purple-100 text-purple-700 font-medium' 
                : 'text-gray-600 hover:bg-gray-100'
            }`}
            data-testid={`billing-tab-${tab.id}`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Dashboard Tab */}
      {activeTab === 'dashboard' && dashboardData && (
        <div className="space-y-6">
          {/* Setup Required Banner */}
          {dashboardData.setup_required && (
            <Card className="p-6 bg-amber-50 border-amber-200">
              <div className="flex items-start gap-4">
                <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-amber-800">Database Setup Required</h3>
                  <p className="text-sm text-amber-700 mt-1">
                    The medical billing tables need to be created in Supabase. 
                    Please run the SQL script from <code className="bg-amber-100 px-1 rounded">/app/backend/sql/medical_billing_schema.sql</code> in your Supabase SQL Editor.
                  </p>
                </div>
              </div>
            </Card>
          )}

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="p-6 bg-gradient-to-br from-purple-50 to-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Billed</p>
                  <p className="text-2xl font-bold text-purple-700">
                    ${dashboardData.total_billed?.toLocaleString() || '0'}
                  </p>
                </div>
                <DollarSign className="w-10 h-10 text-purple-300" />
              </div>
            </Card>

            <Card className="p-6 bg-gradient-to-br from-green-50 to-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Paid</p>
                  <p className="text-2xl font-bold text-green-700">
                    ${dashboardData.total_paid?.toLocaleString() || '0'}
                  </p>
                </div>
                <CheckCircle className="w-10 h-10 text-green-300" />
              </div>
            </Card>

            <Card className="p-6 bg-gradient-to-br from-amber-50 to-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Outstanding</p>
                  <p className="text-2xl font-bold text-amber-700">
                    ${dashboardData.outstanding?.toLocaleString() || '0'}
                  </p>
                </div>
                <Clock className="w-10 h-10 text-amber-300" />
              </div>
            </Card>

            <Card className="p-6 bg-gradient-to-br from-blue-50 to-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Unbilled Shifts</p>
                  <p className="text-2xl font-bold text-blue-700">
                    {dashboardData.unbilled_count || 0}
                  </p>
                </div>
                <AlertTriangle className="w-10 h-10 text-blue-300" />
              </div>
            </Card>
          </div>

          {/* Claims by Status */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">Claims by Status</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
              {['draft', 'pending', 'submitted', 'accepted', 'paid', 'rejected', 'denied'].map(status => (
                <div key={status} className="text-center">
                  <div className={`inline-flex items-center justify-center w-12 h-12 rounded-full ${getStatusColor(status)} mb-2`}>
                    {dashboardData.claims_by_status?.[status] || 0}
                  </div>
                  <p className="text-xs text-gray-600 capitalize">{status}</p>
                </div>
              ))}
            </div>
          </Card>

          {/* Generate Claims Section */}
          <Card className="p-6 border-2 border-dashed border-purple-200 bg-purple-50/50">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5 text-purple-600" />
              Generate Claims from Approved Shifts
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Select a date range to automatically generate billing claims from all approved shifts 
              that haven't been billed yet. Uses the 8-minute rule for unit calculation.
            </p>
            
            <div className="flex flex-wrap gap-4 items-end">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                <input
                  type="date"
                  value={dateRange.start}
                  onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                  className="border rounded-lg px-3 py-2"
                  data-testid="claims-start-date"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                <input
                  type="date"
                  value={dateRange.end}
                  onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                  className="border rounded-lg px-3 py-2"
                  data-testid="claims-end-date"
                />
              </div>
              <Button
                onClick={generateClaims}
                disabled={generatingClaims}
                className="bg-purple-600 hover:bg-purple-700"
                data-testid="generate-claims-btn"
              >
                {generatingClaims ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4 mr-2" />
                    Generate Claims
                  </>
                )}
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Claims Tab */}
      {activeTab === 'claims' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">All Claims</h3>
            <Button variant="outline" onClick={loadClaims}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>

          {claims.length === 0 ? (
            <Card className="p-8 text-center text-gray-500">
              <FileText className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>No claims found. Generate claims from approved shifts.</p>
            </Card>
          ) : (
            <div className="space-y-3">
              {claims.map(claim => (
                <Card 
                  key={claim.id} 
                  className="p-4 hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => setSelectedClaim(selectedClaim?.id === claim.id ? null : claim)}
                  data-testid={`claim-card-${claim.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div>
                        <p className="font-medium">{claim.claim_number}</p>
                        <p className="text-sm text-gray-500">{claim.client_name}</p>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(claim.status)}`}>
                        {claim.status}
                      </span>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-lg">${parseFloat(claim.total_amount).toFixed(2)}</p>
                      <p className="text-xs text-gray-500">{claim.total_units} units</p>
                    </div>
                  </div>

                  {selectedClaim?.id === claim.id && (
                    <div className="mt-4 pt-4 border-t">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-4">
                        <div>
                          <p className="text-gray-500">Payer</p>
                          <p className="font-medium">{claim.payer_name}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Service Dates</p>
                          <p className="font-medium">{claim.service_date_from} - {claim.service_date_to}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Created</p>
                          <p className="font-medium">{new Date(claim.created_at).toLocaleDateString()}</p>
                        </div>
                        {claim.paid_amount && (
                          <div>
                            <p className="text-gray-500">Paid Amount</p>
                            <p className="font-medium text-green-600">${parseFloat(claim.paid_amount).toFixed(2)}</p>
                          </div>
                        )}
                      </div>
                      
                      <div className="flex gap-2">
                        {claim.status === 'draft' && (
                          <Button size="sm" onClick={(e) => { e.stopPropagation(); updateClaimStatus(claim.id, 'pending'); }}>
                            Submit for Review
                          </Button>
                        )}
                        {claim.status === 'pending' && (
                          <Button size="sm" onClick={(e) => { e.stopPropagation(); updateClaimStatus(claim.id, 'submitted'); }}>
                            Mark as Submitted
                          </Button>
                        )}
                        {claim.status === 'submitted' && (
                          <>
                            <Button size="sm" variant="outline" className="text-green-600" onClick={(e) => { e.stopPropagation(); updateClaimStatus(claim.id, 'accepted'); }}>
                              Accepted
                            </Button>
                            <Button size="sm" variant="outline" className="text-red-600" onClick={(e) => { e.stopPropagation(); updateClaimStatus(claim.id, 'rejected'); }}>
                              Rejected
                            </Button>
                          </>
                        )}
                        {claim.status === 'accepted' && (
                          <Button size="sm" className="bg-green-600" onClick={(e) => { e.stopPropagation(); updateClaimStatus(claim.id, 'paid'); }}>
                            Mark as Paid
                          </Button>
                        )}
                        
                        {/* Export Buttons - Always visible */}
                        <div className="ml-auto flex gap-2">
                          <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); exportInvoice(claim.id); }}>
                            <Download className="w-3 h-3 mr-1" />
                            Invoice
                          </Button>
                          <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); exportEDI(claim.id); }}>
                            <Download className="w-3 h-3 mr-1" />
                            EDI 837
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </Card>
              ))}
            </div>
          )}
          
          {/* Batch Export */}
          {claims.length > 0 && (
            <Card className="p-4 mt-4 bg-blue-50 border-blue-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-blue-900">Batch Export</p>
                  <p className="text-sm text-blue-700">Export all pending claims as a single EDI 837P file</p>
                </div>
                <Button onClick={exportBatchEDI} variant="outline" className="border-blue-300 text-blue-700">
                  <Download className="w-4 h-4 mr-2" />
                  Export Batch EDI
                </Button>
              </div>
            </Card>
          )}
        </div>
      )}

      {/* Billing Rates Tab */}
      {activeTab === 'rates' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Billing Rates</h3>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Service Code</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Service Name</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Payer</th>
                  <th className="px-4 py-3 text-right text-sm font-medium text-gray-700">Rate/Unit</th>
                  <th className="px-4 py-3 text-center text-sm font-medium text-gray-700">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {rates.map(rate => (
                  <tr key={rate.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-mono text-purple-600">{rate.service_code}</td>
                    <td className="px-4 py-3">{rate.service_name}</td>
                    <td className="px-4 py-3 text-gray-600">{rate.payer_name}</td>
                    <td className="px-4 py-3 text-right font-medium">${parseFloat(rate.rate_per_unit).toFixed(2)}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`px-2 py-1 rounded-full text-xs ${rate.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                        {rate.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Authorizations Tab */}
      {activeTab === 'authorizations' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Service Authorizations</h3>
          </div>

          {authorizations.length === 0 ? (
            <Card className="p-8 text-center text-gray-500">
              <CheckCircle className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>No authorizations found.</p>
            </Card>
          ) : (
            <div className="space-y-3">
              {authorizations.map(auth => (
                <Card key={auth.id} className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">{auth.service_code} - {auth.payer_name}</p>
                      {auth.authorization_number && (
                        <p className="text-sm text-gray-500">Auth #: {auth.authorization_number}</p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="font-medium">{auth.used_units || 0} / {auth.authorized_units} units</p>
                      <p className="text-xs text-gray-500">{auth.start_date} - {auth.end_date}</p>
                    </div>
                  </div>
                  <div className="mt-2">
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-purple-600 h-2 rounded-full" 
                        style={{ width: `${((auth.used_units || 0) / auth.authorized_units) * 100}%` }}
                      />
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Unit Calculator Tab */}
      {activeTab === 'calculator' && (
        <Card className="p-6 max-w-xl">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Calculator className="w-5 h-5 text-purple-600" />
            8-Minute Rule Calculator
          </h3>
          
          <p className="text-sm text-gray-600 mb-4">
            Healthcare billing uses the 8-minute rule for calculating 15-minute billing units.
            Enter total minutes to see how many units can be billed.
          </p>

          <div className="flex gap-4 items-end mb-6">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">Total Minutes</label>
              <input
                type="number"
                value={unitCalculator.minutes}
                onChange={(e) => setUnitCalculator({ minutes: e.target.value, result: null })}
                placeholder="Enter minutes"
                className="w-full border rounded-lg px-3 py-2"
                data-testid="unit-calculator-input"
              />
            </div>
            <Button onClick={calculateUnits} data-testid="calculate-units-btn">
              Calculate
            </Button>
          </div>

          {unitCalculator.result && (
            <div className="bg-purple-50 rounded-lg p-4 mb-6">
              <div className="text-center mb-4">
                <p className="text-4xl font-bold text-purple-700">{unitCalculator.result.billable_units}</p>
                <p className="text-sm text-purple-600">Billable Units</p>
              </div>
              <p className="text-sm text-gray-700">{unitCalculator.result.explanation}</p>
            </div>
          )}

          <div className="border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left">Time Range</th>
                  <th className="px-4 py-2 text-right">Units</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                <tr><td className="px-4 py-2">0-7 minutes</td><td className="px-4 py-2 text-right font-medium">0</td></tr>
                <tr><td className="px-4 py-2">8-22 minutes</td><td className="px-4 py-2 text-right font-medium">1</td></tr>
                <tr><td className="px-4 py-2">23-37 minutes</td><td className="px-4 py-2 text-right font-medium">2</td></tr>
                <tr><td className="px-4 py-2">38-52 minutes</td><td className="px-4 py-2 text-right font-medium">3</td></tr>
                <tr><td className="px-4 py-2">53-67 minutes</td><td className="px-4 py-2 text-right font-medium">4</td></tr>
                <tr><td className="px-4 py-2">68-82 minutes</td><td className="px-4 py-2 text-right font-medium">5</td></tr>
                <tr><td className="px-4 py-2">83-97 minutes</td><td className="px-4 py-2 text-right font-medium">6</td></tr>
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
